import * as THREE from 'three';

import { sampleColormap } from './colormaps';

/**
 * Vertex-color a triangle-mesh BufferGeometry by elevation or slope.
 * Mutates the input geometry by attaching a `color` attribute the
 * shader can pick up via `vertexColors: true` on the material.
 *
 * Mirrors the LandXML implementation in `LandXML.tsx`, but the
 * elevation range is **passed in** rather than computed per-geometry.
 * That distinction matters for GeoJSON TINs: each triangle is its own
 * BufferGeometry, so a per-geometry min/max would saturate every
 * triangle's gradient locally and erase the layer-wide context. The
 * caller pre-computes a layer-wide `[zMin, zMax]` from the un-shifted
 * Z values and passes it in. SLOPE mode doesn't need the range — it
 * derives [0, π/2] (flat → vertical) from face-normals.
 *
 * Z values in the geometry's `position` attribute are expected to be
 * **already shifted** by the floating-origin (i.e. after
 * `z - mapOrigin.z`). The caller must adjust the supplied
 * `[zMin, zMax]` by the same offset so the t-mapping comes out right
 * — see usage in GeoJson.tsx.
 */
export function applyVectorSurfaceColors(
  geometry: THREE.BufferGeometry,
  mode: 'ELEVATION' | 'SLOPE',
  colormap: string,
  zMinShifted: number,
  zMaxShifted: number,
): void {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!pos) return;
  const count = pos.count;
  const colors = new Float32Array(count * 3);
  const c = new THREE.Color();

  if (mode === 'ELEVATION') {
    const range = Math.max(1e-9, zMaxShifted - zMinShifted);
    for (let i = 0; i < count; i++) {
      const t = Math.min(1, Math.max(0, (pos.getZ(i) - zMinShifted) / range));
      sampleColormap(colormap, t, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  } else {
    // SLOPE: angle between face-normal and the world Z axis. 0 = flat
    // (horizontal triangle), π/2 = vertical wall.
    let normals = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!normals) {
      geometry.computeVertexNormals();
      normals = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
    }
    if (!normals) return;
    for (let i = 0; i < count; i++) {
      const nz = Math.min(1, Math.max(-1, normals.getZ(i)));
      const angle = Math.acos(Math.abs(nz));
      const t = angle / (Math.PI / 2);
      sampleColormap(colormap, t, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  }

  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}
